////////////////////////////////////////////////////////////////
//! Authentication Context
//
//* This context will provide data that pertains to the user
//*   being authenticated, the api being initialized, and all
//*   functions required to control these values
//*
//* This context holds a fairly simple state, and does not
//*   require anything more than useState to handle the state
//*   of the data
//
////////////////////////////////////////////////////////////////

import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import {
  updateAuthTokens,
  initApiClient,
  postPlayerAuth,
  registerUser,
  getClientData,
  getConfigData,
  getWorldThemesAll,
  sendAnalytics,
  getUserConnections
} from 'actions';
import { AuthContext, ClientDataType, ConfigData, UserDataTypes, ConnectionState } from 'types/Auth/AuthTypes';
import { getHoursTimeDiff, parseJwt } from 'utils';
import { AnalyticsInputType, AnalyticsObjectType } from 'types/Analytics/AnalyticsTypes';
import { MenuItemEnabledKeys } from 'types/Application/ApplicationTypes';

export const AuthCtx = createContext({});

const AuthenticationProvider: React.FC = ({ children }) => {
  const [redirectURL, setRedirectURL] = useState<string | null>('');
  const [loginError, setLoginError] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [rememberUser, setRememberUser] = useState(false);
  const [connectionData, setConnectionData] = useState<ConnectionState>({
    connections: [],
    connectionsFetched: false
  });
  const [clientData, setClientData] = useState<ClientDataType>({
    _id: '',
    app_type: 0,
    tenantID: '',
    client: '',
    key: '',
    database: '',
    locations: [],
    hotspots: [],
    signages: [],
    stats: {},
    currencyLevels: {},
    virtualItems: [],
    pointsName: [],
    config: {
      disable: {
        avatars: false,
        catalog: false,
        trivia: false,
        navigation: false,
        users: false,
        login: false,
        leaderboard: false
      },
      multiplayer: 'disabled'
    }
  });

  const [userData, setUserData] = useState<UserDataTypes>({
    xurealID: '',
    username: '',
    organization: {
      default: {
        isEmployee: true,
        currency: 0,
        virtualItems: []
      }
    },
    role: 4,
    currency: 0,
    firstname: '',
    email: ''
  });
  const AVATAR_URL = process.env.REACT_APP_AVATAR_URL;

  //#region Analytics Region

  // This is a generic analytic function
  const handleAppAnalytics = useCallback(
    (eventObj: AnalyticsInputType) => {
      const analyticsUserData = {
        sso_id: userData.xurealID,
        xureal_player_id: userData.xurealID,
        name: '',
        location: clientData.locations[0].name // need to update this to figure current player location
      };
      sendAnalytics({ ...analyticsUserData, ...eventObj });
    },
    [clientData, userData]
  );

  // to avoid infinite looping of player data, this was broken out to its own function
  const handleLoginAnalytics = useCallback((eventObj: AnalyticsObjectType) => {
    sendAnalytics(eventObj);
  }, []);

  //#endregion Analytics Region

  async function retrieveSocialData(userID: string) {
    if (connectionData.connectionsFetched) setConnectionData((prev) => ({ ...prev, connectionsFetched: true }));
    const getConnections = getUserConnections({ xureal_id: userID });
    await Promise.all([getConnections]).then((results: any) => {
      const [connections] = results;
      console.log('connections', connections.content.data);
      setConnectionData((prev) => ({ ...prev, connectionsFetched: true, connections: connections.content.data }));
    });
  }

  const fetchAvatars = async () => {
    const promiseArr = connectionData.connections.map((user) => {
      return fetch(`${AVATAR_URL}avatars/users/${
        user.xureal_id_a === userData.xurealID ? user.xureal_id_b : user.xureal_id_a
      }`).then(res => res.json())
    });
    Promise.all(promiseArr).then((results) => {
      console.log('avatar results', results);
      // const parsedRes = results.map(res => res.json())
      const newConnectionsArr = connectionData.connections.map((user, index) => {
        return { ...user, avatar: results[index][0].thumbnailURL };
      });
      setConnectionData((prev) => {
        return {
          ...prev,
          connections: newConnectionsArr
        };
      });
    });
    //   if (user.avatar === null) {
    //     const avatarURL = `${AVATAR_URL}avatars/users/${user.xureal_id_a === userData.xurealID ? user.xureal_id_b : user.xureal_id_a}`;
    //     const revisedUser: Connection = await fetch(avatarURL)
    //       .then((res) => res.json())
    //       .then((avatarResult) => {
    //         return {...user, avatar: avatarResult[0].thumbnailURL};
    //       })
    //       return revisedUser;
    //   }
    //   return user;
    // };
    // setConnectionData(prev => {
    //   console.log('new avatar state', newState);
    //   return {
    //     ...prev,
    //     connections: newState
    //   }
    // })
  };

  useEffect(() => {
    console.log(connectionData);
    if ((connectionData.connectionsFetched = true)) {
      fetchAvatars();
    }
  }, [connectionData.connections.length]);

  useEffect(() => {
    console.log('connections with avatars', connectionData.connections);
  }, [connectionData]);

  const checkFeatureEnabled = useCallback(
    (flag: MenuItemEnabledKeys) => {
      if (clientData?.config?.disable[flag] || clientData?.config?.disable.all_ui) {
        return false;
      }
      return true;
    },
    [clientData]
  );

  //? This function takes in an object with 2 token strings, and a boolean to signify whether to store the tokens locally
  const handleUserAuth = useCallback(
    (tokenData: { REFRESH_TOKEN: string; JWT_TOKEN: string }, store: boolean) => {
      if (store && rememberUser) {
        localStorage.setItem('demo_auth_token', tokenData.JWT_TOKEN);
        localStorage.setItem('demo_refresh_token', tokenData.REFRESH_TOKEN);
        localStorage.setItem('token_updated_on', JSON.stringify({ date: new Date() }));
      }
      if (store && !rememberUser) {
        sessionStorage.setItem('demo_auth_token', tokenData.JWT_TOKEN);
        sessionStorage.setItem('demo_refresh_token', tokenData.REFRESH_TOKEN);
        sessionStorage.setItem('token_updated_on', JSON.stringify({ date: new Date() }));
      }
      const parsedUserData: UserDataTypes = parseJwt(tokenData.JWT_TOKEN);
      setUserData(parsedUserData);
      setAuthenticated(true);
      const analyticsObj = {
        sso_id: parsedUserData.xurealID,
        location: '',
        xureal_player_id: parsedUserData.xurealID,
        name: '',
        hotspot_id: '',
        content_type_id: '',
        event_category: 'user-login',
        event_action: 'logged',
        label: 'email',
        string_value: parsedUserData.username,
        numeric_value: 0,
        completion_value: 0,
        duration: 0,
        spend_value: 0
      };
      retrieveSocialData(parsedUserData.xurealID);
      handleLoginAnalytics(analyticsObj);
      updateAuthTokens({ JWT_TOKEN: tokenData.JWT_TOKEN, REFRESH_TOKEN: tokenData.REFRESH_TOKEN });
    },
    [handleLoginAnalytics, rememberUser]
  );

  const authorizePlayer = useCallback(
    (player: {
      username?: string;
      password: string;
      email?: string;
      phone?: string;
      firstname?: string;
      lastname?: string;
      login_type?: string;
      role?: number;
    }) => {
      const authData = {
        username: player.username,
        password: player.password,
        email: player.email,
        phone: player.phone,
        firstname: player.firstname,
        lastname: player.lastname,
        login_type: player.login_type,
        role: player.role,
        customID: 'NONE'
      };
      postPlayerAuth(authData)
        .then((data: any) => {
          handleUserAuth({ REFRESH_TOKEN: data.REFRESH_TOKEN, JWT_TOKEN: data.JWT_TOKEN }, true);
        })
        .then(() => {
          redirectURL && (window.location.href = redirectURL);
        })
        .catch((err: any) => {
          setLoginError('Incorrect email/username or password');
          console.error(err);
        });
    },
    [handleUserAuth, redirectURL]
  );

  const handleRegisterUser = useCallback(
    (playerName: string, playerEmail: string, role?: number, data?: any) => {
      const firstname = playerName.split(' ')[0];
      const lastname = playerName.split(' ')[playerName.split(' ').length - 1];

      const playerData = {
        username: playerEmail,
        password: 'xureal2022',
        firstname: firstname || 'NONE',
        lastname: lastname || 'NONE',
        phone: 'NONE',
        email: playerEmail,
        customID: 'NONE',
        role: role,
        data: data || {}
      };

      registerUser(playerData)
        .then((data: any) => {
          authorizePlayer(playerData);
        })
        .catch(console.error);
    },
    [authorizePlayer]
  );

  useEffect(() => {
    if (loginError) {
      const errorTimeout = setTimeout(() => {
        setLoginError('');
      }, 3000);

      return () => {
        clearTimeout(errorTimeout);
      };
    }
  }, [loginError]);

  useEffect(() => {
    if (initialized) return;
    // init api first
    const initApi = initApiClient();
    const getClient = getClientData();
    const getWorlds = getWorldThemesAll();
    // '&url=' + encodeURIComponent("http://a.com/?q=query&n=10")
    console.log('%c1. Init API', 'color: blue; font-weight: bold; font-size: 14px;');
    Promise.all([initApi, getClient, getWorlds])
      .then((values) => {
        const [, clientRes, worldRes] = values;
        const { database, tenantID } = clientRes;

        getConfigData({ application_id: database, tenant_id: tenantID })
          .then((configRes: any) => {
            const appConfig: ConfigData = JSON.parse(configRes);

            const worldTheme =
              appConfig.worldTheme &&
              worldRes.themes.find((worldData: any) => worldData.theme_slug === appConfig.worldTheme)
                ? worldRes.themes.find((worldData: any) => worldData.theme_slug === appConfig.worldTheme)
                : '';
            setClientData({
              ...clientRes,
              config: {
                ...appConfig,
                worldTheme: worldTheme.theme_slug,
                multiplayer: appConfig.multiplayer ? appConfig.multiplayer : 'disabled'
              },
              allThemes: worldRes.themes
            });
            setInitialized(true);
          })
          .catch((err: any) => console.error('Config failure', err));
      })
      .catch((err) => console.error('API init failure', err));
  }, [initialized]);

  useEffect(() => {
    if (!initialized) return;
    if (!checkFeatureEnabled('login')) {
      setAuthenticated(true);
      return;
    }
    if (localStorage.getItem('demo_auth_token') && localStorage.getItem('demo_refresh_token')) {
      if (localStorage.getItem('token_updated_on')) {
        const startDate = JSON.parse(localStorage.getItem('token_updated_on') || JSON.stringify({ date: new Date(1) }));
        const timeDiff = getHoursTimeDiff(startDate.date, new Date());
        if (timeDiff > 72) {
          localStorage.clear();
          return;
        }
      }
      let tokenData = {
        JWT_TOKEN: localStorage.getItem('demo_auth_token') || '',
        REFRESH_TOKEN: localStorage.getItem('demo_refresh_token') || ''
      };
      handleUserAuth({ REFRESH_TOKEN: tokenData.REFRESH_TOKEN, JWT_TOKEN: tokenData.JWT_TOKEN }, false);
    }
    if (sessionStorage.getItem('demo_auth_token') && sessionStorage.getItem('demo_refresh_token')) {
      let tokenData = {
        JWT_TOKEN: sessionStorage.getItem('demo_auth_token') || '',
        REFRESH_TOKEN: sessionStorage.getItem('demo_refresh_token') || ''
      };
      handleUserAuth({ REFRESH_TOKEN: tokenData.REFRESH_TOKEN, JWT_TOKEN: tokenData.JWT_TOKEN }, false);
    }
  }, [initialized, clientData, handleUserAuth, checkFeatureEnabled]);

  return (
    <AuthCtx.Provider
      value={{
        authenticated,
        setAuthenticated,
        initialized,
        setInitialized,
        handleRegisterUser,
        authorizePlayer,
        loginError,
        userData,
        clientData,
        checkFeatureEnabled,
        setClientData,
        handleAppAnalytics,
        handleUserAuth,
        setRememberUser,
        setRedirectURL,
        redirectURL,
        connectionData
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthCtx) as AuthContext;
};

export default AuthenticationProvider;
